import type { UserGroupType } from '@navio/nsdk';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiLink, NoddiTooltip } from 'noddi-ui-web';
import {
  getUserGroupContactSecondaryLabels,
  getUserGroupTypeIcon,
  getUserGroupTypeLabel,
  resolveUserGroupType
} from 'noddi-util';
import type { ReactNode } from 'react';
import { customerInfoName, type CustomerInfoCellUserGroup } from './customerInfo';

type Props = {
  userGroup: CustomerInfoCellUserGroup;
  /** When set, only the name is linked — keeps the type tooltip outside the anchor. */
  nameTo?: string;
  nameTarget?: '_blank' | '_self';
  /** Off in cmdk rows where tooltips fight keyboard navigation. */
  showTypeTooltip?: boolean;
  children?: ReactNode;
};

export const CustomerInfoCell = ({ userGroup, nameTo, nameTarget, showTypeTooltip = true, children }: Props) => {
  const type: UserGroupType = resolveUserGroupType(userGroup);
  const typeLabel: string = getUserGroupTypeLabel(userGroup);
  const name: string = customerInfoName(userGroup);
  const secondaryLabels: string[] = getUserGroupContactSecondaryLabels({
    isPersonal: userGroup.isPersonal ?? false,
    users: userGroup.users ?? []
  });

  const typeIcon = (
    <NoddiIcon name={getUserGroupTypeIcon(type)} size='small' className='mt-0.5 shrink-0 text-muted-foreground' />
  );

  return (
    <div className='flex items-start gap-2'>
      {showTypeTooltip ? <NoddiTooltip trigger={typeIcon} content={typeLabel} label={typeLabel} /> : typeIcon}
      <div className='flex min-w-0 flex-col'>
        {nameTo ? (
          <NoddiLink
            variant='link'
            className='h-auto max-w-full p-0 font-normal whitespace-normal no-underline'
            spanClassName='truncate underline underline-offset-2'
            to={nameTo}
            target={nameTarget}
          >
            {name}
          </NoddiLink>
        ) : (
          <span className='truncate'>{name}</span>
        )}
        {secondaryLabels.map((line) => (
          <span key={line} className='truncate text-xs text-muted-foreground'>
            {line}
          </span>
        ))}
        {children}
      </div>
    </div>
  );
};
